import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {
  ImagePreviewType,
  ProductImageGrid,
  ProductImageGridImages,
} from '@aboutyou/ui-components';
import React, {FC, useState} from 'react';
import {useBadgesList} from '../../../features/BadgeList/useBadgesList';
import {useShopTheLookScrollManager} from '../ShopTheLookScrollIntoView/useShopTheLookScrollManager';
import {useSimilarItemsScrollManager} from '../SimilarItemsScrollIntoView/useSimilarItemsScrollManager';
import {SimilarProductsFab} from './SimilarProductsFab';

const MAX_NUM_OF_IMAGES = 11;

export interface ProductImageGridViewProps {
  inactive?: boolean;
  onImageClick: (index: number) => void;
  gridColumns?: number;
  previewImage?: ImagePreviewType;
}

export const ProductImageGridView: FC<
  Omit<
    GetProductBulkResponse['imagesSection'],
    'images' | 'qualitativeBadges' | 'videoButton'
  > &
    ProductImageGridViewProps
> = ({
  badges,
  gallery,
  product: productData,
  inactive,
  onImageClick,
  gridColumns,
  previewImage,
}) => {
  const {isSoldOut} = productData;
  const {isActive: isPriceOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );

  const {scrollToShopTheLook} = useShopTheLookScrollManager();
  const {scrollToSimilarItems} = useSimilarItemsScrollManager();

  const badgesList = useBadgesList({
    badges,
    campaignSale: null,
    sale: null,
    showPriceOverhaulV2SaleLabel: isPriceOverhaulV2Active,
  });

  const images = gallery?.elements.reduce((acc, element, index) => {
    if (index > MAX_NUM_OF_IMAGES - 1) {
      return acc;
    }

    switch (element.type?.$case) {
      case 'image': {
        const image = element.type.image.image;
        acc.push({
          alt: image.description,
          padded: image.isEnclosed,
          src: image.src,
        });
        break;
      }
      case 'shopTheLook': {
        const {image, label} = element.type.shopTheLook;
        acc.push({
          alt: image.description,
          padded: image.isEnclosed,
          src: image.src,
          shopTheLookLabel: label,
        });
        break;
      }
      case 'generatedImage': {
        const image = element.type.generatedImage;
        acc.push({
          alt: image.description,
          padded: false,
          src: image.url,
        });
        break;
      }
      case 'video':
      default:
        break;
    }

    return acc;
  }, [] as ProductImageGridImages);

  const [showScrollToSimilarItemsButton, setShowScrollToSimilarItemsButton] =
    useState(true);

  const handleScrollToSimilarItems = () => {
    setShowScrollToSimilarItemsButton(false);
    scrollToSimilarItems();
  };
  return (
    <ProductImageGrid
      badgesList={badgesList}
      images={images}
      gridColumns={gridColumns}
      inactive={inactive}
      onImageClick={onImageClick}
      onShopTheLookClick={scrollToShopTheLook}
      soldOut={isSoldOut}
      previewImage={previewImage}
      soldOutOverlayButton={
        showScrollToSimilarItemsButton && (
          <SimilarProductsFab
            onSimilarItemsClick={handleScrollToSimilarItems}
          />
        )
      }
    />
  );
};
